import { InstallButton } from './InstallButton';
import { INSTALL_COPY } from './installCopy';

/**
 * Pure presenter Экрана 2.0.PWA «Загрузи приложение» (PRD §2.1) для не-iOS
 * пользователей. iOS-ветка рендерится Container'ом отдельно через
 * `IOSInstructions`. Standalone / installed → `<Navigate />` тоже в Container'е,
 * поэтому Presenter получает только три «активных» state из P-021:
 *
 * - `'idle'`     — prompt захвачен браузером, кнопка кликабельна.
 * - `'loading'`  — `triggerInstall()` в полёте: spinner, aria-busy, блок кликов.
 * - `'error'`    — последняя попытка завершилась `dismissed` / `unavailable`:
 *                  показываем retry-message + retry CTA + skip CTA.
 * - `'empty'`    — `beforeinstallprompt` не выстрелил и не iOS (Firefox /
 *                  устаревший Safari / desktop Chrome без installability):
 *                  unavailable-message + disabled CTA + skip CTA.
 *
 * Success state P-021 — это `<Navigate />` в Container'е (после `installed=true`),
 * Presenter его не видит.
 *
 * P-019 (vertical buttons): на error и empty — две кнопки в столбик. На idle —
 * одна (skip скрыт, чтобы не отвлекать от happy-path установки).
 */

export type InstallScreenState = 'idle' | 'loading' | 'error' | 'empty';

export interface InstallScreenViewProps {
  state: InstallScreenState;
  /** Запускает (или повторяет) install — Container оборачивает `triggerInstall()`. */
  onInstall: () => void;
  /** Skip → Container навигирует на `/onboarding/lang-city`. */
  onSkip: () => void;
}

export function InstallScreenView({ state, onInstall, onSkip }: InstallScreenViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <div className="h-16 flex items-center px-4 bg-header border-b border-header-border">
        <span
          className="font-display text-h-logo text-ink"
          style={{ letterSpacing: '0.02em', lineHeight: 1 }}
        >
          Помощник мебельщика
        </span>
      </div>

      <section
        className="px-4 py-6 flex flex-col gap-5"
        aria-labelledby="install-title"
        data-testid="install-screen-section"
      >
        <h1 id="install-title" className="font-display text-h1 text-ink">
          {INSTALL_COPY.title}
        </h1>

        {state === 'error' ? (
          <p role="alert" className="text-error text-sm leading-snug">
            {INSTALL_COPY.errorRetryMessage}
          </p>
        ) : null}

        {state === 'empty' ? (
          <p
            role="status"
            aria-live="polite"
            className="text-muted text-base leading-snug"
            data-testid="install-empty-message"
          >
            {INSTALL_COPY.unavailableMessage}
          </p>
        ) : null}

        <InstallButton
          label={state === 'error' ? INSTALL_COPY.retryCta : INSTALL_COPY.installCta}
          loadingLabel={INSTALL_COPY.installingLabel}
          loading={state === 'loading'}
          disabled={state === 'empty'}
          onClick={onInstall}
        />

        {state === 'error' || state === 'empty' ? (
          <button
            type="button"
            onClick={onSkip}
            data-testid="install-skip-button"
            className={
              'w-full font-medium text-ink bg-transparent ' +
              'border-[1.5px] border-accent rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
              'active:opacity-90 transition-opacity'
            }
            style={{ minHeight: 52, fontSize: 17 }}
          >
            {INSTALL_COPY.skipCta}
          </button>
        ) : null}
      </section>
    </main>
  );
}
